import { ref, updateMetadata } from 'firebase/storage';

import { Injectable } from '@angular/core';
import { IStorageFile, StoragePaths } from '@shared/models/storage';
import { StorageService } from '@shared/services/storage/storage.service';

@Injectable({
	providedIn: 'root',
})
export class JobCardStorageService extends StorageService {
	/**
	 * Uploads an Attachment (e.g., quote, invoice) for an assessment.
	 * @param organisationId - The organisation's unique ID.
	 * @param leadRequestId - The lead's unique ID.
	 * @param assessmentId - The assessment's unique ID.
	 * @param file - The file to be uploaded.
	 * @returns A promise that resolves with the download URL of the uploaded file.
	 */
	uploadAssessmentAttachment(
		organisationId: string,
		leadRequestId: string,
		assessmentId: string,
		file: File,
		uploadedByName: string,
	): Promise<string> {
		const fileName = file.name;
		const path = StoragePaths.assessmentAttachment(organisationId, leadRequestId, assessmentId, fileName);
		return this.uploadFile(path, file, {
			uploadedByName,
		});
	}

	/**
	 * Fetches a job card's attachments.
	 * @param organisationId - The organisation's unique ID.
	 * @param leadRequestId - The lead's unique ID.
	 * @param assessmentId - The assessment's unique ID.
	 * @returns A promise that resolves with the download URL of the customer signature.
	 */
	async getJobCardAttachments(organisationId: string, leadRequestId: string, assessmentId: string): Promise<Array<IStorageFile>> {
		const folderPath = StoragePaths.assessmentAttachmentsFolder(organisationId, leadRequestId, assessmentId);
		const filesWithMetadata = await this.listFilesInFolderWithMetadata(folderPath);

		if (filesWithMetadata.length === 0) {
			return [];
		}

		return filesWithMetadata;
	}

	async deleteJobCardAttachment(path: string): Promise<void> {
		return await this.deleteFile(path);
	}

	async editJobCardMetaData(
		path: string,
		customMetadata: {
			[key: string]: string;
		},
	) {
		const storageRef = ref(this.storage, path);
		return await updateMetadata(storageRef, {
			customMetadata,
		});
	}

	/**
	 * Locks all attachments of a job card by updating their metadata with "locked": "true".
	 * @param organisationId - The organisation's unique ID.
	 * @param leadRequestId - The lead's unique ID.
	 * @param assessmentId - The assessment's unique ID.
	 */
	async lockJobCardAttachments(organisationId: string, leadRequestId: string, assessmentId: string): Promise<void> {
		const attachments = await this.getJobCardAttachments(organisationId, leadRequestId, assessmentId);

		const updatePromises = attachments.map((file) => {
			return this.editJobCardMetaData(file.fullPath, {
				...(file.customMetadata || {}),
				locked: 'true',
			});
		});

		await Promise.all(updatePromises);
	}
}
